import React, { useEffect, useMemo, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment-timezone';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router';
import { user } from '../../../redux/user';
import { managementCompany, setManagementCompany } from '../../../redux/managementCompany';
import {
	viewStateForWorkforceProfessionals,
	updateProfessionalsViewStateForManagementCompany,
	updateLocationsViewStateForManagementCompany,
	viewStateForWorkforceLocations,
	updateActiveTab,
	activeTab
} from '../../../redux/flexWorkforceView';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import FlexProfessionalsList from './FlexProfessionalsList';
import FlexLocationsList from './FlexLocations';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';
import arrow from '../../../assets/images/keyboard_arrow_down.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/download.svg';
import utils from '../../../utils/utils';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { PERMISSION, PRIVATE_PATH } from '../../../constants';
import TextSearchComponent from '../../../components/SearchComponent/TextSearchComponent';

import './FlexWorkforceScreen.scss';

const DEFAULT_FILTERS = { global: { value: '', matchMode: FilterMatchMode.CONTAINS } };

const FlexWorkforceScreen = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const workersDataTableRef = useRef(null);
	const locationsDataTableRef = useRef(null);

	const userData = useSelector(user, shallowEqual);
	const managementCompanyData = useSelector(managementCompany, shallowEqual);

	const activeTabIndex = useSelector(activeTab);
	const currentManagementCompanyId = managementCompanyData?.id;
	const professionalsViewState = useSelector(viewStateForWorkforceProfessionals(currentManagementCompanyId));
	const professionalFilters = professionalsViewState?.filters || DEFAULT_FILTERS;
	const professionalSearchTerm = professionalFilters.global?.value || '';
	const locationsViewState = useSelector(viewStateForWorkforceLocations(currentManagementCompanyId));
	const locationFilters = locationsViewState?.filters || DEFAULT_FILTERS;
	const locationSearchTerm = locationFilters.global?.value || '';

	const setProfessionalFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(professionalFilters) : updater;
		dispatch(
			updateProfessionalsViewStateForManagementCompany({
				managementCompanyId: currentManagementCompanyId,
				filters: next
			})
		);
	};

	const setLocationFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(locationFilters) : updater;
		dispatch(
			updateLocationsViewStateForManagementCompany({ managementCompanyId: currentManagementCompanyId, filters: next })
		);
	};

	const managementCompaniesOptions = useMemo(() => {
		const uniqueManagementCompanyIds = new Set();
		const options = [];

		userData?.facilitiesEnabled?.forEach((facility) => {
			const mcId = facility.management_company_id;
			if (
				!uniqueManagementCompanyIds.has(mcId) &&
				utils.isAuthorizedExact(
					MANAGEMENT_COMPANIES_PERMISSIONS.WORKFORCE_ACTION_VIEW,
					userData.managementCompanyPermissions[mcId]
				) &&
				facility.allow_workforce_page
			) {
				uniqueManagementCompanyIds.add(mcId);
				options.push({
					id: mcId,
					name: facility.management_company_name
				});
			}
		});

		return options.sort((a, b) => a.name.localeCompare(b.name));
	}, [userData]);

	const handleManagementCompanyChange = (newManagementCompany) => {
		dispatch(setManagementCompany(newManagementCompany));
	};

	useEffect(() => {
		if (!managementCompaniesOptions.length) {
			history.push(PRIVATE_PATH.SHIFTS);
			return;
		}

		if (!managementCompanyData?.id && managementCompaniesOptions.length) {
			handleManagementCompanyChange(managementCompaniesOptions[0]);
		}
	}, [managementCompaniesOptions]);

	const ManagementCompanyDropdown = () => {
		if (managementCompaniesOptions?.length < 2) {
			return <div className="title">Workforce - {managementCompanyData?.name}</div>;
		}
		return (
			<div className="dropdown-management-company">
				<Dropdown>
					<Dropdown.Toggle>
						<div className="title">Workforce - {managementCompanyData?.name}</div>
						<img src={arrow} alt="expand" className="keyboard-arrow-down-icon" />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						{managementCompaniesOptions.map((option) => (
							<Dropdown.Item
								className="dropdown-management-company__item"
								onClick={() => handleManagementCompanyChange(option)}
								key={`management-company-${option.id}`}
							>
								{option.name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	const exportCSV = () => {
		if (activeTabIndex === 0) {
			workersDataTableRef.current.exportCSV();
		} else {
			locationsDataTableRef.current.exportCSV();
		}
	};
	const onProfessionalSearch = (value) => {
		setProfessionalFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const onLocationSearch = (value) => {
		setLocationFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const exportFilename = `${activeTabIndex ? 'Locations' : 'Workforce'}_${managementCompanyData.name}_${moment().format(
		'YYYY-MM-DD_hh-mm'
	)}`;

	const downloadPermission = activeTabIndex ? PERMISSION.WORKFORCE_DOWNLOAD_LOCATIONS : PERMISSION.WORKFORCE_DOWNLOAD;

	const showLocationsTab = utils.isAuthorizedExact(
		PERMISSION.WORKFORCE_LOCATIONS_VIEW,
		userData.managementCompanyPermissions[managementCompanyData.id]
	);

	const handleTabChange = (event) => {
		dispatch(updateActiveTab(event.index));
		if (event.index) {
			onProfessionalSearch('');
		} else {
			onLocationSearch('');
		}
	};

	return (
		<WithOnlyOnDesktop>
			<div className="flex-workforce-screen">
				<div className="title-wrapper">
					<ManagementCompanyDropdown />
					{activeTabIndex === 0 ? (
						<TextSearchComponent
							placeholder="Search for..."
							className="search-container"
							searchValue={professionalSearchTerm}
							setSearchValue={onProfessionalSearch}
						/>
					) : (
						<TextSearchComponent
							placeholder="Search for..."
							className="search-container"
							searchValue={locationSearchTerm}
							setSearchValue={onLocationSearch}
						/>
					)}
				</div>
				<div className="btn-download-container">
					<IsAuthorizedForManagementCompany
						exactPermission={downloadPermission}
						managementCompanyId={managementCompanyData.id}
					>
						<Button
							type="button"
							className="btn-download-list"
							label="Download report"
							outlined
							onClick={exportCSV}
							data-pr-tooltip="Download"
							icon={() => <DownloadIcon />}
						/>
					</IsAuthorizedForManagementCompany>
				</div>

				<TabView className="workforce-tab" activeIndex={activeTabIndex} onTabChange={handleTabChange}>
					<TabPanel header="Internal">
						{managementCompanyData?.id && (
							<FlexProfessionalsList
								managementCompany={managementCompanyData}
								dataTableRef={workersDataTableRef}
								exportFilename={exportFilename}
								filters={professionalFilters}
								setFilters={setProfessionalFilters}
							/>
						)}
					</TabPanel>
					{showLocationsTab && (
						<TabPanel header="Locations">
							{managementCompanyData?.id && (
								<FlexLocationsList
									managementCompany={managementCompanyData}
									dataTableRef={locationsDataTableRef}
									exportFilename={exportFilename}
									filters={locationFilters}
								/>
							)}
						</TabPanel>
					)}
				</TabView>
			</div>
		</WithOnlyOnDesktop>
	);
};

export default FlexWorkforceScreen;
